/**
 * ServicesPage.jsx - Garage Services Public Page
 * 
 * Lists all services offered by a specific garage.
 * Accessed via /:garageSlug/services
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Spinner } from '../components/ui/spinner';
import { formatCurrency } from '../lib/i18n';
import { Wrench, Clock, CheckCircle, Phone, Globe, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default service categories
const defaultCategories = [
  { value: 'all', label: 'All', labelBn: 'সব' },
  { value: 'maintenance', label: 'Maintenance', labelBn: 'রক্ষণাবেক্ষণ' },
  { value: 'repair', label: 'Repair', labelBn: 'মেরামত' },
  { value: 'washing', label: 'Washing', labelBn: 'ওয়াশিং' },
  { value: 'electrical', label: 'Electrical', labelBn: 'ইলেকট্রিক্যাল' },
  { value: 'custom', label: 'Custom Work', labelBn: 'কাস্টম কাজ' },
];

export default function ServicesPageLegacy() {
  const { garageSlug } = useParams();
  const navigate = useNavigate();
  const { t, language, toggleLanguage } = useLanguage();
  
  const [services, setServices] = useState([]);
  const [garageInfo, setGarageInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [notFound, setNotFound] = useState(false);
  const [categories, setCategories] = useState(defaultCategories);
  const [badgeOpacity, setBadgeOpacity] = useState(1);
  const scrollTimer = useRef(null);

  // Scroll-fade for watermark badge
  useEffect(() => {
    const onScroll = () => {
      setBadgeOpacity(0.15);
      if (scrollTimer.current) clearTimeout(scrollTimer.current);
      scrollTimer.current = setTimeout(() => setBadgeOpacity(1), 400);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); if (scrollTimer.current) clearTimeout(scrollTimer.current); };
  }, []);

  useEffect(() => {
    if (garageSlug) {
      fetchData();
    }
  }, [garageSlug]);

  const fetchData = async () => {
    try {
      const [garageRes, servicesRes, catRes] = await Promise.all([
        axios.get(`${API}/tenants/${garageSlug}/public`),
        axios.get(`${API}/tenants/${garageSlug}/services`),
        axios.get(`${API}/tenants/${garageSlug}/categories/services`).catch(() => ({ data: [] }))
      ]);
      setGarageInfo(garageRes.data);
      setServices(servicesRes.data);
      if (catRes.data.length > 0) {
        const existingValues = new Set(defaultCategories.map(c => c.value));
        const custom = catRes.data
          .filter(c => !existingValues.has(c.value))
          .map(c => ({ value: c.value, label: c.name, labelBn: c.name_bn || c.name }));
        setCategories([...defaultCategories, ...custom]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (error.response?.status === 404) {
        setNotFound(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // Redirect if no slug
  if (!garageSlug) {
    navigate('/');
    return null;
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Spinner size="lg" />
      </div>
    );
  }

  // Not found state
  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-4">
        <h1 className="text-4xl font-bold text-slate-900 mb-4">
          {language === 'en' ? 'Garage Not Found' : 'গ্যারেজ পাওয়া যায়নি'}
        </h1>
        <Link to="/">
          <Button className="bg-emerald-600 hover:bg-emerald-700">
            {language === 'en' ? 'Go to MotoPotti' : 'মোটোপটিতে যান'}
          </Button>
        </Link>
      </div>
    );
  }

  const garage = garageInfo || {};
  const garageName = language === 'bn' && garage.name_bn ? garage.name_bn : garage.name;
  const basePath = `/${garageSlug}`;

  // Filter services by category
  const filteredServices = activeCategory === 'all' 
    ? services 
    : services.filter(s => s.category === activeCategory);

  // Call garage
  const callGarage = () => {
    window.location.href = `tel:${garage.phone}`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to={basePath} className="flex items-center gap-2" data-testid="logo-link">
              {garage.logo_url ? (
                <img src={garage.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
              ) : (
                <div className="w-10 h-10 bg-blue-800 rounded-lg flex items-center justify-center">
                  <Wrench className="w-6 h-6 text-white" />
                </div>
              )}
              <span className="text-xl font-bold text-slate-900 tracking-tight font-['Barlow_Condensed']">
                {garageName}
              </span>
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <Link to={basePath} className="text-sm font-medium text-slate-600 hover:text-blue-800">
                {language === 'en' ? 'Home' : 'হোম'}
              </Link>
              <Link to={`${basePath}/services`} className="text-sm font-medium text-blue-800">
                {language === 'en' ? 'Services' : 'সার্ভিস'}
              </Link>
              <Link to={`${basePath}/store`} className="text-sm font-medium text-slate-600 hover:text-blue-800">
                {language === 'en' ? 'Parts' : 'পার্টস'}
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={toggleLanguage} className="gap-2">
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline">{language === 'en' ? 'বাংলা' : 'English'}</span>
              </Button>
              <Button onClick={callGarage} className="bg-blue-800 hover:bg-blue-900 gap-2">
                <Phone className="w-4 h-4" />
                <span className="hidden sm:inline">{language === 'en' ? 'Call Now' : 'কল করুন'}</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-12 bg-gradient-to-br from-blue-900 to-blue-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to={basePath} className="inline-flex items-center text-blue-200 hover:text-white mb-4 gap-1">
            <ArrowLeft className="w-4 h-4" />
            {language === 'en' ? 'Back to Home' : 'হোমে ফিরে যান'}
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold text-white font-['Barlow_Condensed']">
            {language === 'en' ? 'Our Services' : 'আমাদের সার্ভিস'}
          </h1>
          <p className="text-blue-100 mt-2 max-w-2xl">
            {language === 'en' 
              ? 'Professional motorcycle service and repair. All work guaranteed.'
              : 'পেশাদার মোটরসাইকেল সার্ভিস এবং মেরামত। সব কাজের গ্যারান্টি।'}
          </p>
        </div>
      </section>

      {/* Services Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Filter */}
          <Tabs value={activeCategory} onValueChange={setActiveCategory} className="mb-8">
            <TabsList className="flex flex-wrap gap-2 h-auto bg-transparent">
              {categories.map((cat) => (
                <TabsTrigger 
                  key={cat.value} 
                  value={cat.value}
                  className="data-[state=active]:bg-blue-800 data-[state=active]:text-white"
                  data-testid={`category-${cat.value}`}
                >
                  {language === 'bn' ? cat.labelBn : cat.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Services Grid */}
          {filteredServices.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              {language === 'en' ? 'No services in this category' : 'এই ক্যাটাগরিতে কোনো সার্ভিস নেই'}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredServices.map((service) => (
                <Card key={service.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="font-bold text-xl text-slate-900">
                        {language === 'bn' && service.name_bn ? service.name_bn : service.name}
                      </h3>
                      <Badge variant="secondary" className="capitalize">
                        {service.category}
                      </Badge>
                    </div>
                    
                    <p className="text-slate-600 mb-4 min-h-[3rem]">
                      {service.description}
                    </p>
                    
                    <div className="flex items-center gap-2 text-slate-500 mb-4">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">{service.estimated_time}</span>
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t">
                      <span className="text-2xl font-bold text-blue-800">
                        {formatCurrency(service.price, language)}
                      </span>
                      <div className="flex items-center gap-1 text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm">{language === 'en' ? 'Available' : 'উপলব্ধ'}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4 font-['Barlow_Condensed']">
            {language === 'en' ? 'Ready to Book a Service?' : 'সার্ভিস বুক করতে প্রস্তুত?'}
          </h2>
          <p className="text-slate-600 mb-6">
            {language === 'en' 
              ? 'Call us now or visit our garage. No appointment needed!'
              : 'এখনই কল করুন বা আমাদের গ্যারেজে আসুন। অ্যাপয়েন্টমেন্টের প্রয়োজন নেই!'}
          </p>
          <Button 
            size="lg" 
            onClick={callGarage}
            className="bg-blue-800 hover:bg-blue-900 gap-2"
          >
            <Phone className="w-5 h-5" />
            {garage.phone}
          </Button>
        </div>
      </section>

      {/* MotoPotti Floating Badge — iOS Liquid Glass */}
      <Link
        to="/"
        className="fixed bottom-5 right-5 z-50 group flex items-center gap-1.5 hover:gap-2.5 rounded-full py-1.5 pl-1.5 pr-2.5 hover:pr-3.5 transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] cursor-pointer"
        style={{
          opacity: badgeOpacity,
          transition: 'opacity 0.5s ease, gap 0.5s cubic-bezier(0.34,1.56,0.64,1), padding 0.5s cubic-bezier(0.34,1.56,0.64,1)',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0.45) 40%, rgba(240,249,255,0.55) 100%)',
          backdropFilter: 'blur(50px) saturate(220%) brightness(1.15) contrast(1.05)',
          WebkitBackdropFilter: 'blur(50px) saturate(220%) brightness(1.15) contrast(1.05)',
          border: '0.5px solid rgba(255,255,255,0.7)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.06), 0 1.5px 6px rgba(0,0,0,0.03), inset 0 1.5px 0 rgba(255,255,255,0.9), inset 0 -0.5px 0 rgba(0,0,0,0.04)',
        }}
        title="MotoPotti"
      >
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
          style={{
            background: 'linear-gradient(145deg, #34d399 0%, #0d9488 100%)',
            boxShadow: '0 2px 8px rgba(13,148,136,0.3), inset 0 1px 0 rgba(255,255,255,0.3)',
          }}
        >
          <Wrench className="w-3 h-3 text-white drop-shadow-sm" />
        </div>
        <div className="flex flex-col leading-none">
          <span className="text-[9px] text-slate-400/70 font-medium">{language === 'en' ? 'Powered by' : 'পরিচালিত'}</span>
          <span className="text-[12px] font-bold text-slate-600 tracking-tight">MotoPotti</span>
        </div>
      </Link>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-slate-400">
            © {new Date().getFullYear()} {garageName}. {language === 'en' ? 'Powered by' : 'পরিচালিত'}{' '}
            <Link to="/" className="text-emerald-400 hover:text-emerald-300">MotoPotti</Link>
          </p>
        </div>
      </footer>
    </div>
  );
}
